import React, { useEffect } from "react";
import { formatCurrency } from "../../utils/helpers";

/**
 * SiteDrillDown component - Shows detailed breakdown for a selected site
 * @param {Object} props
 * @param {Object} props.site - Site object with site name
 * @param {Array} props.circuits - Array of circuit records for this site
 * @param {Function} props.onClose - Callback to close drill-down
 */
const SiteDrillDown = ({ site, circuits = [], onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  if (!site || !circuits || circuits.length === 0) return null;

  const totalMRC = circuits.reduce((sum, circuit) => sum + parseFloat(circuit.mrc || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4 overflow-y-auto animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col my-auto animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Site Details</h3>
            <p className="text-sm text-gray-600 mt-1 font-medium">{site.site}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Summary */}
        <div className="px-6 py-5 bg-gradient-to-b from-gray-50 to-white border-b border-gray-200">
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Total Circuits</p>
              <p className="text-2xl font-bold text-blue-600 tabular-nums">{circuits.length}</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Total MRC</p>
              <p className="text-2xl font-bold text-emerald-600 tabular-nums">{formatCurrency(totalMRC)}</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Average MRC</p>
              <p className="text-2xl font-bold text-violet-600 tabular-nums">{formatCurrency(circuits.length > 0 ? totalMRC / circuits.length : 0)}</p>
            </div>
          </div>
        </div>

        {/* Circuits List */}
        <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50/50">
          <div className="space-y-3">
            {circuits.map((circuit, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md hover:border-blue-200 transition-all">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Circuit ID</p>
                    <p className="text-sm font-medium text-gray-900 truncate tabular-nums">{circuit.circuit_id || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Provider</p>
                    <p className="text-sm font-medium text-gray-900">{circuit.provider || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Service Type</p>
                    <p className="text-sm font-medium text-gray-900">{circuit.service_type || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">MRC</p>
                    <p className="text-base font-bold text-blue-700 tabular-nums">{formatCurrency(parseFloat(circuit.mrc || 0))}</p>
                  </div>
                </div>
                {circuit.bandwidth && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">Bandwidth: <span className="font-medium text-gray-800">{circuit.bandwidth}</span></p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white flex justify-end">
          <button onClick={onClose} className="px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm transition-all">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteDrillDown;
